/**
 * This class manages adding and removing global (window) event handlers for touch/mouse move and up.
 * This is useful for e.g. implementing resize handles or drag behaviors.
 *
 * Relying on element-level events is ineffective, because they are only triggered while the pointer stays within the bounds of the element.
 */
export class GlobalPointerEventCapturer {
  private listenersAdded: boolean;
  private onPointerMove: (e: MouseEvent | TouchEvent) => void;
  private onPointerUp: (e: MouseEvent | TouchEvent) => void;

  public constructor(
    onPointerMove: (e: MouseEvent | TouchEvent) => void,
    onPointerUp: (e: MouseEvent | TouchEvent) => void
  ) {
    this.listenersAdded = false;
    this.onPointerMove = onPointerMove;
    this.onPointerUp = onPointerUp;
  }

  public addListeners(): void {
    if (this.listenersAdded) {
      return;
    }

    window.addEventListener('touchend', this.onPointerUp, true);
    window.addEventListener('mouseup', this.onPointerUp, true);
    if (window !== window.top) { // TFSID 1265762: release capture if mousing out of an iframe
      document.addEventListener('touchcancel', this.onPointerUp, true);
      document.addEventListener('mouseleave', this.onPointerUp);
    }
    // `passive: false` necessary for TFSID 798261 + https://bugs.webkit.org/show_bug.cgi?id=182521
    window.addEventListener('touchmove', this.onPointerMove, { capture: true, passive: false });
    window.addEventListener('mousemove', this.onPointerMove, { capture: true, passive: false });

    this.listenersAdded = true;
  }

  public removeListeners(): void {
    if (!this.listenersAdded) {
      return;
    }

    window.removeEventListener('touchend', this.onPointerUp, true);
    window.removeEventListener('mouseup', this.onPointerUp, true);
    document.removeEventListener('touchcancel', this.onPointerUp, true);
    document.removeEventListener('mouseleave', this.onPointerUp);
    window.removeEventListener('touchmove', this.onPointerMove, { capture: true });
    window.removeEventListener('mousemove', this.onPointerMove, { capture: true });

    this.listenersAdded = false;
  }
}
